/**
 * HomeCareersSection.jsx — Feb 2026
 * Compact "We're hiring" band on the homepage. Fetches active jobs from
 * /api/careers/jobs and renders them as apply cards. Hides itself entirely
 * when there are no active openings, so the homepage never shows an empty
 * "we're hiring" row.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Briefcase, MapPin, ArrowRight, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  en: {
    eyebrow: "We're hiring",
    heading: "Join Karine's team",
    lede: "We're growing across our Ottawa, Kanata and Carleton Place clinics — nurses, medical aestheticians, and admin support. If you love beauty, wellness and treating clients like family, we'd love to hear from you.",
    openPositions: "Open positions",
    apply: "Apply now",
    all: "See all openings & apply",
    empty: "",
  },
  fr: {
    eyebrow: "Nous embauchons",
    heading: "Rejoignez l'équipe de Karine",
    lede: "Nous grandissons dans nos cliniques d'Ottawa, Kanata et Carleton Place — infirmières, esthéticiennes médicales et soutien administratif. Si vous aimez la beauté, le bien-être et traiter les clients comme de la famille, on veut vous rencontrer.",
    openPositions: "Postes ouverts",
    apply: "Postuler",
    all: "Voir tous les postes & postuler",
    empty: "",
  },
};

export default function HomeCareersSection() {
  const { language } = useLanguage();
  const t = T[language] || T.en;
  const [jobs, setJobs] = useState(null); // null = loading, [] = none

  useEffect(() => {
    axios
      .get(`${API}/careers/jobs`)
      .then((r) => setJobs((r.data?.jobs || []).filter((j) => (j.status || "active") === "active")))
      .catch(() => setJobs([]));
  }, []);

  // Hide entirely when we're still loading OR no active openings.
  if (!jobs || jobs.length === 0) return null;

  const careersHref = language === "fr" ? "/fr/carrieres" : "/careers";

  return (
    <section
      className="relative py-16 px-6 overflow-hidden"
      data-testid="home-careers-section"
      aria-labelledby="home-careers-heading"
    >
      {/* Warm cream backdrop with subtle emerald wash */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-[#fbfaf6] to-amber-50" aria-hidden="true" />
      <div className="absolute inset-0 opacity-[0.03]" aria-hidden="true" style={{
        backgroundImage: "radial-gradient(circle at 20% 30%, #065f46 0, transparent 40%), radial-gradient(circle at 80% 70%, #b45309 0, transparent 40%)",
      }} />

      <div className="relative max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-12 items-start">
          {/* Left column — headline */}
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-800 text-white text-[10px] uppercase tracking-widest font-bold mb-4">
              <Sparkles className="w-3 h-3" /> {t.eyebrow}
            </div>
            <h2 id="home-careers-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight mb-4">
              {t.heading}
            </h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed max-w-xl mb-5">{t.lede}</p>
            <Link
              to={careersHref}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-800 text-white text-sm font-bold uppercase tracking-wide hover:bg-emerald-900 transition min-h-[44px]"
              data-testid="home-careers-see-all"
            >
              {t.all} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Right column — open positions list */}
          <div>
            <p className="uppercase tracking-[0.28em] text-[10px] text-emerald-700 font-bold mb-3">
              {t.openPositions} · {jobs.length}
            </p>
            <ul className="space-y-3">
              {jobs.slice(0, 4).map((j) => {
                const title = language === "fr" ? (j.title_fr || j.title_en) : (j.title_en || j.title_fr);
                return (
                  <li key={j.id}>
                    <Link
                      to={j.apply_url ? (language === "fr" ? (j.apply_url_fr || j.apply_url) : j.apply_url) : careersHref}
                      className="group flex items-center justify-between gap-3 p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition min-h-[44px]"
                      data-testid={`home-careers-job-${j.id}`}
                    >
                      <div className="min-w-0">
                        <h3 className="font-serif text-lg text-slate-900 truncate">{title}</h3>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />{j.location}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />{j.employment_type}
                          </span>
                        </div>
                      </div>
                      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-emerald-800 group-hover:text-emerald-950">
                        {t.apply} <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            {jobs.length > 4 && (
              <p className="text-xs text-slate-500 mt-3 italic">
                + {jobs.length - 4} more · <Link to={careersHref} className="underline hover:text-emerald-800">see all</Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
